import axios from "axios";
import { useReducer, useEffect, useState } from "react";

const ACTIONS = {
  SELECT_FOLDER: "select-folder",
  UPDATE_FOLDER: "update-folder",
  SET_CHILD_FOLDERS: "set-child-folders",
  SET_CHILD_FILES: "set-child-files",
  SET_LOADING: "set-loading",
};

const reducer = (state, { type, payload }) => {
  switch (type) {
    case ACTIONS.SELECT_FOLDER:
      return {
        ...state,
        folderPath: payload.folderPath,
        folder: null,
        childFolders: [],
        childFiles: [],
        loading: true,
      };
    case ACTIONS.UPDATE_FOLDER:
      return {
        ...state,
        folder: payload.folder,
        loading: false,
      };
    case ACTIONS.SET_CHILD_FOLDERS:
      return {
        ...state,
        childFolders: payload.childFolders,
      };
    case ACTIONS.SET_CHILD_FILES:
      return {
        ...state,
        childFiles: payload.childFiles,
      };
    case ACTIONS.SET_LOADING:
      return {
        ...state,
        loading: payload.loading,
      };
    default:
      return state;
  }
};

const fetchFolderData = async (userID) => {
  try {
    const response = await axios.post(
      `${process.env.REACT_APP_BASE_URL}/doc-vault/get-structure`,
      { userID },
      { withCredentials: true }
    );

    if (response.status === 200) {
      return response.data.structure;
    } else {
      console.warn("Non-200 response:", response.data.message);
      return null;
    }
  } catch (error) {
    console.error("Error fetching folder structure:", error);
    return null;
  }
};

const getFolderByPath = (folder, path) => {
  if (path === "") return folder;
  const segments = path.split("/");
  let current = folder;
  for (const seg of segments) {
    current = current?.childFolders?.find((f) => f.name === seg);
    if (!current) return null;
  }
  return current;
};

const useFolder = (folderPath = "", userID, trigger) => {
  const [state, dispatch] = useReducer(reducer, {
    folderPath,
    folder: null,
    childFolders: [],
    childFiles: [],
    loading: true,
  });
  
  const [fullStructure, setFullStructure] = useState(null);

  useEffect(() => {
    const loadStructure = async () => {
      if (!userID) return;
      dispatch({ type: ACTIONS.SET_LOADING, payload: { loading: true } });

      const structure = await fetchFolderData(userID);
      if (structure) {
        setFullStructure(structure);
      }

      dispatch({ type: ACTIONS.SET_LOADING, payload: { loading: false } });
    };

    loadStructure();
  }, [userID, trigger]);

  useEffect(() => {
    if (!fullStructure) return;

    dispatch({ type: ACTIONS.SELECT_FOLDER, payload: { folderPath } });

    const selectedFolder = getFolderByPath(fullStructure, folderPath);
    if (selectedFolder) {
      dispatch({
        type: ACTIONS.UPDATE_FOLDER,
        payload: { folder: selectedFolder },
      });

      const childFolders = selectedFolder.childFolders || [];
      const childFiles = selectedFolder.childFiles || [];

      // If there are no child folders or files, ensure loading is set to false
      if (childFolders.length === 0 && childFiles.length === 0) {
        dispatch({ type: ACTIONS.SET_LOADING, payload: { loading: false } });
      } else {
        dispatch({
          type: ACTIONS.SET_CHILD_FOLDERS,
          payload: { childFolders },
        });
        dispatch({
          type: ACTIONS.SET_CHILD_FILES,
          payload: { childFiles },
        });
      }
    } else {
      dispatch({ type: ACTIONS.SET_LOADING, payload: { loading: false } });
    }
  }, [folderPath, fullStructure]);

  return state;
};

export default useFolder;
